import React, { useState } from "react";
import { Popover, Checkbox } from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import "./CompactFilters.css";

/**
 * A small, self-contained multi-select dropdown styled to match the other
 * compact filter boxes in the queue toolbar. Fully controlled — the caller
 * owns the selected values — so there's no internal state to drift out of
 * sync with its source of truth, unlike the MUI-based MultiSelect this
 * replaces. Shares CompactFilters.css with CompactSelect so every filter
 * box in the toolbar stays visually identical by construction.
 */
function CompactMultiSelect({ options = [], value = [], onChange, placeholder = "Select", nodata = "No options" }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const toggleValue = (optValue) => {
    const next = value.includes(optValue)
      ? value.filter((v) => v !== optValue)
      : [...value, optValue];
    onChange(next);
  };

  const summary =
    value.length === 0
      ? placeholder
      : value.length <= 3
      ? value.join(", ")
      : `${value.length} selected`;

  return (
    <>
      <button
        type="button"
        className={`compact-filter-trigger${value.length ? " has-value" : ""}`}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <span className="compact-filter-trigger-label">{summary}</span>
        <span className="compact-filter-trigger-icons">
          <KeyboardArrowDown className="compact-filter-chevron" />
        </span>
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: "compact-filter-menu" }}
      >
        {options.length > 0 ? (
          options.map((opt) => {
            const optValue = opt.value ?? opt.id;
            return (
              <label key={optValue} className="compact-filter-option">
                <Checkbox
                  size="small"
                  checked={value.includes(optValue)}
                  onChange={() => toggleValue(optValue)}
                />
                <span>{opt.name}</span>
              </label>
            );
          })
        ) : (
          <div className="compact-filter-empty">{nodata}</div>
        )}
      </Popover>
    </>
  );
}

export default CompactMultiSelect;
